import type { Lang } from '../types';

export interface CleaningCalendarText {
  scheduleLabel: string;
  loading: string;
  notFoundTitle: string;
  notFoundDesc: string;
  noTasksTitle: string;
  noTasksDesc: string;
  todayBadge: string;
  taskCount: (n: number) => string;
  statusPending: string;
  statusInProgress: string;
  statusDone: string;
  leaving: string;
  arriving: string;
  arrival: string;
  guests: string;
  startCleaning: string;
  markDone: string;
  editNote: string;
  addNote: string;
  notePlaceholder: string;
  save: string;
  cancel: string;
  startedAt: string;
  completedAt: string;
  actionFailed: string;
  notesSaveFailed: string;
  todayAllClear: string;
  todayAllClearSub: string;
}

export const cleaningCalendarText: Record<Lang, CleaningCalendarText> = {
  cs: {
    scheduleLabel:   'Úklidový plán',
    loading:         'Načítám plán…',
    notFoundTitle:   'Plán nenalezen',
    notFoundDesc:    'Odkaz může být neplatný nebo neaktivní.',
    noTasksTitle:    'Žádné úkoly',
    noTasksDesc:     'Ve vašem okně nejsou naplánovány žádné odhlášení.',
    todayBadge:      'DNES',
    taskCount:       n => n === 1 ? '1 úkol' : n >= 2 && n <= 4 ? `${n} úkoly` : `${n} úkolů`,
    statusPending:   'Čeká',
    statusInProgress:'Probíhá',
    statusDone:      'Hotovo',
    leaving:         'Odchází',
    arriving:        'Přichází',
    arrival:         'Příchod',
    guests:          'hostů',
    startCleaning:   '▶ Začít úklid',
    markDone:        '✓ Označit jako hotové',
    editNote:        '✏ Upravit poznámku',
    addNote:         '+ Přidat poznámku',
    notePlaceholder: 'Přidejte poznámku… (max 100 znaků)',
    save:            'Uložit',
    cancel:          'Zrušit',
    startedAt:       'Zahájeno:',
    completedAt:     'Dokončeno:',
    actionFailed:    'Akce selhala',
    notesSaveFailed: 'Nepodařilo se uložit poznámku',
    todayAllClear:   'Dnes žádný úklid 🎉',
    todayAllClearSub:'Užijte si volný den!',
  },
  en: {
    scheduleLabel:   'Cleaning Schedule',
    loading:         'Loading schedule…',
    notFoundTitle:   'Schedule not found',
    notFoundDesc:    'The link may be invalid or inactive.',
    noTasksTitle:    'No tasks scheduled',
    noTasksDesc:     'No checkouts are scheduled in your window.',
    todayBadge:      'TODAY',
    taskCount:       n => n === 1 ? '1 task' : `${n} tasks`,
    statusPending:   'Pending',
    statusInProgress:'In Progress',
    statusDone:      'Done',
    leaving:         'Leaving',
    arriving:        'Arriving',
    arrival:         'Arrival',
    guests:          'guests',
    startCleaning:   '▶ Start Cleaning',
    markDone:        '✓ Mark as Done',
    editNote:        '✏ Edit Note',
    addNote:         '+ Add Note',
    notePlaceholder: 'Add a note… (max 100 chars)',
    save:            'Save',
    cancel:          'Cancel',
    startedAt:       'Started:',
    completedAt:     'Completed:',
    actionFailed:    'Action failed',
    notesSaveFailed: 'Failed to save note',
    todayAllClear:   'No cleaning today 🎉',
    todayAllClearSub:'Enjoy your day off!',
  },
  vi: {
    scheduleLabel:   'Lịch dọn phòng',
    loading:         'Đang tải…',
    notFoundTitle:   'Không tìm thấy lịch',
    notFoundDesc:    'Liên kết có thể không hợp lệ hoặc đã hết hiệu lực.',
    noTasksTitle:    'Không có công việc',
    noTasksDesc:     'Không có lịch trả phòng trong khoảng thời gian của bạn.',
    todayBadge:      'HÔM NAY',
    taskCount:       n => `${n} việc`,
    statusPending:   'Chờ',
    statusInProgress:'Đang làm',
    statusDone:      'Xong',
    leaving:         'Trả phòng',
    arriving:        'Nhận phòng',
    arrival:         'Đến lúc',
    guests:          'khách',
    startCleaning:   '▶ Bắt đầu dọn',
    markDone:        '✓ Đánh dấu xong',
    editNote:        '✏ Sửa ghi chú',
    addNote:         '+ Thêm ghi chú',
    notePlaceholder: 'Thêm ghi chú… (tối đa 100 ký tự)',
    save:            'Lưu',
    cancel:          'Hủy',
    startedAt:       'Bắt đầu:',
    completedAt:     'Hoàn thành:',
    actionFailed:    'Thao tác thất bại',
    notesSaveFailed: 'Không thể lưu ghi chú',
    todayAllClear:   'Hôm nay không có dọn phòng 🎉',
    todayAllClearSub:'Tận hưởng ngày nghỉ nào!',
  },
  uk: {
    scheduleLabel:   'Графік прибирання',
    loading:         'Завантаження…',
    notFoundTitle:   'Графік не знайдено',
    notFoundDesc:    'Посилання може бути недійсним або неактивним.',
    noTasksTitle:    'Немає запланованих завдань',
    noTasksDesc:     'У вашому вікні немає запланованих виїздів.',
    todayBadge:      'СЬОГОДНІ',
    taskCount:       n => n === 1 ? '1 завдання' : n >= 2 && n <= 4 ? `${n} завдання` : `${n} завдань`,
    statusPending:   'Очікує',
    statusInProgress:'Виконується',
    statusDone:      'Готово',
    leaving:         'Виїжджає',
    arriving:        'Приїжджає',
    arrival:         'Прибуття',
    guests:          'гостей',
    startCleaning:   '▶ Почати прибирання',
    markDone:        '✓ Позначити готовим',
    editNote:        '✏ Редагувати нотатку',
    addNote:         '+ Додати нотатку',
    notePlaceholder: 'Додайте нотатку… (макс. 100 символів)',
    save:            'Зберегти',
    cancel:          'Скасувати',
    startedAt:       'Розпочато:',
    completedAt:     'Завершено:',
    actionFailed:    'Помилка дії',
    notesSaveFailed: 'Не вдалося зберегти нотатку',
    todayAllClear:   'Сьогодні прибирання немає 🎉',
    todayAllClearSub:'Насолоджуйтесь вільним днем!',
  },
};
